"use client";

import { LogOut, Users, Wallet } from "lucide-react";
import Link from "next/link";

import { formatCurrency } from "@/lib/format";

import { Button } from "@/components/ui/button";

import { logout } from "@/features/auth/api/auth-client";
import { useCurrentUser } from "@/features/auth/hooks/use-current-user";
import { useNotificationCounts } from "@/features/notifications/hooks/use-notification-counts";

/** First letter of the username, for the avatar circle. */
function initial(username: string) {
  return username.trim().charAt(0).toUpperCase() || "?";
}

/**
 * Right-hand user bar in the header. Signed out → Sign in / Sign up. Signed in →
 * plain inline items matching the legacy top navbar: avatar, username, the
 * account balance pill, the referrals/collaborators badge, and a logout button —
 * no dropdown (the account
 * links now live in the in-nav "My account" menu, `AccountNavMenu`) and no
 * notification badge. The collaborators count reads from the same
 * `GET /dashboard/notification` payload the legacy navbar used
 * (`useNotificationCounts` → `referrals.total`, the legacy `total_ref`) and, like
 * the legacy `FaUsers` pill, always shows the number (even 0). Renders the
 * signed-out state while the session is still loading to avoid layout shift.
 */
export function AccountMenu() {
  const { user, isAuthenticated, isLoading } = useCurrentUser();
  const { data: counts } = useNotificationCounts();

  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link href="/login">Sign in</Link>
        </Button>
        <Button
          asChild
          size="sm"
          // Hide the secondary CTA until we know the user is signed out, so a
          // brief loading flash doesn't show then retract it.
          className={isLoading ? "pointer-events-none opacity-0" : undefined}
        >
          <Link href="/signup">Sign up</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 text-sm">
      <Link
        href="/myaccount"
        className="flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span
          aria-hidden="true"
          className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-sm font-semibold text-primary-foreground"
        >
          {initial(user.username)}
        </span>
        <span className="hidden font-medium text-foreground sm:inline">
          {user.username}
        </span>
      </Link>

      {/* Account balance — the legacy navbar's green money pill, restyled for the
       * redesign. Reads the same `balance` field the legacy navbar showed and
       * links to /myaccount. Always rendered (even $0.00), matching legacy. */}
      <Link
        href="/myaccount"
        title="Account balance"
        aria-label={`Account balance: ${formatCurrency(counts?.balance ?? 0)}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-elevated px-2.5 py-1 text-xs font-semibold tabular-nums text-foreground ring-1 ring-hairline transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Wallet aria-hidden="true" className="size-3.5 text-success-text" />
        <span>{formatCurrency(counts?.balance ?? 0)}</span>
      </Link>

      <Link
        href="/invite"
        title="Referrals"
        aria-label={`${counts?.referrals.total ?? 0} collaborators`}
        className="inline-flex items-center gap-1 rounded-full bg-elevated px-2 py-1 text-xs font-semibold tabular-nums text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span>{counts?.referrals.total ?? 0}</span>
        <Users aria-hidden="true" className="size-3.5" />
      </Link>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => void logout()}
        className="gap-1.5 text-muted-foreground"
      >
        <LogOut aria-hidden="true" className="size-4" />
        <span className="hidden sm:inline">Logout</span>
      </Button>
    </div>
  );
}
